import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, MapPin } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { EntityCombobox } from "@/components/ui/EntityCombobox";
import { readBack } from "@/lib/nav";

const EMPTY = {
  customer_id: "",
  name: "",
  address: { street: "", city: "", postcode: "", country: "" },
  on_site_contact_name: "",
  on_site_contact_phone: "",
  on_site_contact_email: "",
  access_notes: "",
  notes: "",
  active: true,
};

export default function SiteForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preselectedCustomer = params.get("customer_id") || "";
  const backCtx = readBack(params);

  const [form, setForm] = useState({ ...EMPTY, customer_id: preselectedCustomer });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Cancel target: explicit ?back=, else natural parent (customer detail#sites) if prefilled, else index.
  const cancelTo = backCtx?.to
    || (isEdit ? `/sites/${id}` : preselectedCustomer ? `/customers/${preselectedCustomer}?tab=sites` : "/sites");

  useEffect(() => {
    api.get("/customers", { params: { active: true, limit: 500 } })
      .then(({ data }) => setCustomers(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/sites/${id}`);
        setForm({ ...EMPTY, ...data, address: { ...EMPTY.address, ...(data.address || {}) } });
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/sites", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateAddress = (patch) => setForm((prev) => ({ ...prev, address: { ...prev.address, ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/sites/${id}`, form);
        toast.success("Site updated");
        navigate(backCtx?.to || `/sites/${data.id}`);
      } else {
        const { data } = await api.post("/sites", form);
        toast.success("Site created");
        navigate(backCtx?.to || `/sites/${data.id}`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="site-form-page">
      <Breadcrumbs
        items={[
          { label: "Sites", to: "/sites" },
          ...(isEdit ? [{ label: form.name || "Site", to: `/sites/${id}` }, { label: "Edit" }] : [{ label: "New" }]),
        ]}
      />
      <PageHeader
        testId="site-form-header"
        title={isEdit ? `Edit ${form.name}` : "New site"}
        actions={<Button asChild variant="outline" size="sm" data-testid="site-form-back"><Link to={cancelTo}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>}
      />

      <form onSubmit={submit} className="space-y-6 max-w-4xl">
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="cust">Customer</Label>
              <EntityCombobox
                items={customers}
                value={form.customer_id || ""}
                onChange={(v) => update({ customer_id: v })}
                placeholder="Select customer"
                emptyMessage="No customers match."
                testid="site-form-customer"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Site name</Label>
              <Input id="name" value={form.name} onChange={(e) => update({ name: e.target.value })} required data-testid="site-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-name">On-site contact name</Label>
              <Input id="contact-name" value={form.on_site_contact_name} onChange={(e) => update({ on_site_contact_name: e.target.value })} data-testid="site-form-contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-phone">On-site contact phone</Label>
              <Input id="contact-phone" value={form.on_site_contact_phone} onChange={(e) => update({ on_site_contact_phone: e.target.value })} data-testid="site-form-contact-phone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contact-email">On-site contact email</Label>
              <Input id="contact-email" type="email" value={form.on_site_contact_email} onChange={(e) => update({ on_site_contact_email: e.target.value })} data-testid="site-form-contact-email" />
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium">Address</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 md:col-span-2"><Label htmlFor="street">Street</Label><Input id="street" value={form.address.street} onChange={(e) => updateAddress({ street: e.target.value })} data-testid="site-form-street" /></div>
            <div className="space-y-1.5"><Label htmlFor="city">City</Label><Input id="city" value={form.address.city} onChange={(e) => updateAddress({ city: e.target.value })} data-testid="site-form-city" /></div>
            <div className="space-y-1.5"><Label htmlFor="postcode">Postcode</Label><Input id="postcode" value={form.address.postcode} onChange={(e) => updateAddress({ postcode: e.target.value })} data-testid="site-form-postcode" /></div>
            <div className="space-y-1.5 md:col-span-2"><Label htmlFor="country">Country</Label><Input id="country" value={form.address.country} onChange={(e) => updateAddress({ country: e.target.value })} data-testid="site-form-country" /></div>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-sm font-medium">Access notes &amp; comments</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="access">Access notes</Label>
              <Textarea id="access" rows={4} value={form.access_notes} onChange={(e) => update({ access_notes: e.target.value })} data-testid="site-form-access-notes" placeholder="Open hours, security, key contacts…" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => update({ notes: e.target.value })} data-testid="site-form-notes" />
            </div>
            <div className="flex items-center justify-between rounded-sm border border-border bg-card p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <div className="text-xs text-muted-foreground">Inactive sites are hidden from the default list.</div>
              </div>
              <Switch checked={form.active} onCheckedChange={(v) => update({ active: v })} data-testid="site-form-active" />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(cancelTo)} data-testid="site-form-cancel">Cancel</Button>
          <Button type="submit" disabled={saving || !form.customer_id} data-testid="site-form-submit"><Save className="h-4 w-4" />{saving ? "Saving…" : isEdit ? "Save changes" : "Create site"}</Button>
        </div>
      </form>
    </div>
  );
}
